// Client for journey-service — the order backbone.
//
// Same shared `platform` cluster, base path /journey. What it returns is only
// the PERSISTED half of a journey: the stage snapshot, manual completions with
// who did them, and the timeline. Evidence-backed stages are resolved locally
// by `resolveJourney` from facts this screen already holds, which is why there
// is no "get me the resolved stages" call here — one implementation, shared.

import type {
  CancellationExit, CancellationRecord, JourneyEvent, JourneyRecord, SettlementLine, SettlementTotals, StageGate,
} from '@platform/shared-types';

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

export function resolveJourneyHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/journey-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/journey`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/journey`;
  return `https://platform.api.dev.${apex}/journey`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_JOURNEY_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveJourneyHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

/**
 * Thrown by `req()` on any non-2xx response. Carries the real HTTP status as
 * `.status` — same pattern as `ActivationTransportError` in billingApi.ts —
 * because the message alone is not enough to tell a genuine "not found" apart
 * from a server error: journey-service's `notFound()` answers with a normal,
 * human-readable `{ error }` body, so the message never falls back to the
 * generic `journey ${status}` text a caller might otherwise grep for.
 */
export interface JourneyApiError extends Error {
  status: number;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try { detail = (await res.json())?.error || ''; } catch { /* non-JSON */ }
    const err = new Error(detail || `journey ${res.status}`) as JourneyApiError;
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

export type ManualCompletions = Record<string, {
  name: string; at: number; staffId?: string; roleId?: string;
  attachment?: { key: string; name?: string; contentType?: string };
  /** Completed by hand over an evidence stage; `note` is the reason. */
  override?: boolean;
  note?: string;
}>;

/** The shape every cancellation route answers with — one derivation, never twice. */
export interface CancellationResponse {
  cancellation: CancellationRecord;
  holds: StageGate[];
  totals: SettlementTotals | null;
}

export const journeyApi = {
  /** Idempotent — the id derives from the subject. Safe on every screen open. */
  ensure: (tenantId: string, subjectId: string, opts?: { subjectType?: string; locationId?: string }) =>
    req<{ journey: JourneyRecord; manual: ManualCompletions; created: boolean }>('/admin/journeys/ensure', {
      method: 'POST',
      body: JSON.stringify({ tenantId, subjectId, subjectType: opts?.subjectType || 'order', locationId: opts?.locationId }),
    }),

  get: (journeyId: string) =>
    req<{ journey: JourneyRecord; manual: ManualCompletions; timeline: JourneyEvent[] }>(
      `/admin/journeys/${encodeURIComponent(journeyId)}`),

  /** A person completing a stage the system cannot observe. Attributed server-side. */
  complete: (
    journeyId: string, stageId: string, note?: string,
    /** Uploaded proof — an S3 key under `{tenantId}/admin/orders/`. */
    attachment?: { key: string; name?: string; contentType?: string },
    /** Completing an EVIDENCE stage by hand — `note` is then the required reason. */
    override?: boolean,
  ) =>
    req<{ journey: JourneyRecord; manual: ManualCompletions }>(
      `/admin/journeys/${encodeURIComponent(journeyId)}/complete`,
      { method: 'POST', body: JSON.stringify({ stageId, note, ...(attachment ? { attachment } : {}), ...(override ? { override: true } : {}) }) }),

  /** Undo writes a NEW record; it never erases who said what. */
  undo: (journeyId: string, stageId: string, reason?: string) =>
    req<{ journey: JourneyRecord; manual: ManualCompletions }>(
      `/admin/journeys/${encodeURIComponent(journeyId)}/undo`,
      { method: 'POST', body: JSON.stringify({ stageId, reason }) }),

  message: (journeyId: string, body: string, opts?: { internal?: boolean; stageId?: string; audience?: string[] }) =>
    req<{ event: JourneyEvent }>(`/admin/journeys/${encodeURIComponent(journeyId)}/messages`,
      { method: 'POST', body: JSON.stringify({ body, ...opts }) }),

  timeline: (journeyId: string, limit = 200) =>
    req<{ timeline: JourneyEvent[] }>(`/admin/journeys/${encodeURIComponent(journeyId)}/timeline?limit=${limit}`),

  /** Everything this customer has ever done — the two-year view. */
  tenantHistory: (tenantId: string, opts?: { limit?: number; after?: string }) =>
    req<{ timeline: JourneyEvent[]; next?: string }>(
      `/admin/journeys/tenant/${encodeURIComponent(tenantId)}?limit=${opts?.limit || 100}${opts?.after ? `&after=${encodeURIComponent(opts.after)}` : ''}`),

  /** One device's or one model's whole history — `unit:u_1`, `model:mdl_k10`. */
  subjectHistory: (subject: string, limit = 100) =>
    req<{ subject: string; timeline: JourneyEvent[] }>(
      `/admin/journeys/subject/${encodeURIComponent(subject)}?limit=${limit}`),

  /**
   * Two doors, one path: the customer requests, or we initiate. Idempotent —
   * opening a cancellation that is already live hands back the live one.
   */
  openCancellation: (journeyId: string, origin: 'customer' | 'rubytill', reason?: string) =>
    req<CancellationResponse>(`/admin/journeys/${encodeURIComponent(journeyId)}/cancellation`, {
      method: 'POST', body: JSON.stringify({ origin, reason }),
    }),

  readCancellation: (journeyId: string) =>
    req<CancellationResponse>(`/admin/journeys/${encodeURIComponent(journeyId)}/cancellation`),

  /** A settlement is APPENDED, never edited — `propose` is what sends it to the customer. */
  saveSettlement: (
    journeyId: string,
    payload: { exit: CancellationExit; lines: SettlementLine[]; note?: string; propose?: boolean },
  ) =>
    req<CancellationResponse>(`/admin/journeys/${encodeURIComponent(journeyId)}/cancellation/settlement`, {
      method: 'POST', body: JSON.stringify(payload),
    }),

  /** The order goes straight back on the road — nothing was re-derived or thrown away. */
  withdrawCancellation: (journeyId: string, reason?: string) =>
    req<CancellationResponse>(`/admin/journeys/${encodeURIComponent(journeyId)}/cancellation/withdraw`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),

  /** Records that the customer accepted the standing settlement. Refuses unless one has been proposed. */
  acceptCancellation: (journeyId: string) =>
    req<CancellationResponse>(`/admin/journeys/${encodeURIComponent(journeyId)}/cancellation/accept`,
      { method: 'POST' }),

  /**
   * The LAST move — only after the caller has already cancelled the order.
   * Refuses with a 409 unless the body says `orderCancelled: true`, which is
   * why this is never called on its own: see `hardwareApi.cancelOrder` and
   * the two-call sequence in `CancellationPanel`.
   */
  closeCancellation: (journeyId: string) =>
    req<CancellationResponse>(`/admin/journeys/${encodeURIComponent(journeyId)}/cancellation/close`,
      { method: 'POST', body: JSON.stringify({ orderCancelled: true }) }),
};
